import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { combineAll } from '@datachain/ui-sdk/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
	catchError,
	concatMap,
	endWith,
	exhaustMap,
	filter,
	from,
	map,
	of,
	switchMap,
	tap,
	withLatestFrom,
} from 'rxjs';

import { NgCommService } from '../../ajs-ng-communication.service';
import { AjsScopeService } from '../../ajs-scope.service';
import { AjsStateService } from '../../ajs-state.service';
import { states } from '../../core/application.states';
import { AppSelector } from '../../core/store';
import { updateEntitiesCountForDepotFailure } from '../../depot/store';
import { DataFrameRequester } from '../../requesters/core/data-frame.requester';
import { EntityRequester } from '../../requesters/entity/entity.requester';
import { EntityItemEntity } from '../components/entity-list/entity-item.entity';
import { EntityListEntity } from '../components/entity-list/entity-list.entity';
import { EntityAdapter } from '../entity.adapter';
import {
	bulkDelete,
	constructEntityList,
	constructEntityListFail,
	constructEntityListSuccess,
	bulkDeleteFail,
	bulkDeleteSuccess,
	createDatablocksFromEntities,
	createDatablocksFromEntitiesFail,
	createDatablocksFromEntitiesSuccess,
	deleteEntity,
	deleteEntityFail,
	deleteEntitySuccess,
	fetchEntityListFail,
	fetchEntityListSuccess,
	navigateToCreateFromEntity,
	navigateToEntity,
	navigateToEntityCreate,
	navigateToEntityDuplicate,
	persistenceActionFinish,
	persistenceActionsAccepted,
	persistEntities,
	persistEntityFail,
	persistEntityStarted,
	persistEntityStopped,
	refreshEntityList,
	repersistEntities,
	repersistEntityFail,
	repersistEntityStarted,
	repersistEntityStopped,
	stopPersistence,
	toggleEntityStatus,
	trackAction,
	unpersistEntities,
	unpersistEntityFail,
	unpersistEntityStarted,
	unpersistEntityStopped,
	updateEntitiesFrameStatus,
	updateEntitiesFrameStatusSuccess,
} from './entity.actions';
import { EntitySelector } from './entity.selector';

type EntityJobSocketResponseType = {
	element_id: number;
	type: string;
	message: string;
	data:
		| {
				job_status: string;
				client_id: string;
				job_id: string;
				persisted: boolean;
				persistence_date: number;
				persistence_execution_time: number;
				persistence_size: number;
				current_repartition_number: number;
		  }
		| undefined;
};
@Injectable()
export class EntityEffects {
	// ##########################################################
	// Navigation Effects
	// ##########################################################

	public navigateToEntityCreate = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToEntityCreate),
				tap(() => this.stateService.go(states.EntityCreate))
			),
		{
			dispatch: false,
		}
	);

	public navigateToEntity = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToEntity),
				tap((a) =>
					this.stateService.go(states.EntityEdit, {
						entiteId: `${a.id}`,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	public navigateToEntityDuplicate = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToEntityDuplicate),
				tap((a) =>
					this.stateService.go(states.EntityDuplicate, {
						duplicatedId: `${a.id}`,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	public navigateToCreateFromEntity = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToCreateFromEntity),
				tap((a) =>
					this.stateService.go(states.DatablockFromEntity, {
						entiteId: `${a.id}`,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	// ##########################################################
	// Business Effects
	// ##########################################################

	public constructEntityList$ = createEffect(() =>
		this.action$.pipe(
			ofType(constructEntityList),
			map(() =>
				constructEntityListSuccess({
					entityList: this.entityAdapter.generateEntityListStructure(),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					constructEntityListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public getEntitiesFrameStatus$ = createEffect(() =>
		this.action$.pipe(
			ofType(updateEntitiesFrameStatus),
			switchMap((action) =>
				this.entityRequester.getEntitiesFrameStatus(
					action.ids,
					!action.shouldReload
				)
			),
			withLatestFrom(this.entitySelector.getEntityList$()),
			map(([response, currentList]) =>
				updateEntitiesFrameStatusSuccess({
					entityList: this.entityAdapter.updateListWithEntitiesFrameStatus(
						currentList,
						response
					),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					updateEntitiesCountForDepotFailure({
						error,
					})
				);
				return caught;
			})
		)
	);

	public listenToWS$ = createEffect(() =>
		this.action$.pipe(
			ofType(constructEntityListSuccess),
			switchMap(() =>
				from(
					this.ajsScopeService.listenOnRootScope<EntityJobSocketResponseType>(
						'job_global'
					)
				)
			),
			filter(
				(stompResponse) =>
					stompResponse?.type === 'PersistFrame' ||
					stompResponse?.type === 'UnPersistFrame' ||
					stompResponse?.type === 'RepersistFrame'
			),
			withLatestFrom(this.entitySelector.getEntityList$()),
			tap(([stompResponse, entityList]) => {
				if (stompResponse?.data?.job_status.toLocaleLowerCase() === 'running') {
					if (stompResponse?.type === 'PersistFrame') {
						this.store.dispatch(
							persistEntityStarted({
								entityId: stompResponse.element_id,
								jobId: stompResponse.data.job_id,
							})
						);
					}
					if (stompResponse?.type === 'UnPersistFrame') {
						this.store.dispatch(
							unpersistEntityStarted({
								entityId: stompResponse.element_id,
								jobId: stompResponse.data.job_id,
							})
						);
					}
					if (stompResponse?.type === 'RepersistFrame') {
						this.store.dispatch(
							repersistEntityStarted({
								entityId: stompResponse.element_id,
								jobId: stompResponse.data.job_id,
							})
						);
					}
				} else if (
					stompResponse?.data?.job_status.toLocaleLowerCase() === 'error'
				) {
					const { elements } = entityList;
					const entity = elements
						.toList()
						.toArray()
						.find((e) => e.code === stompResponse?.message);
					if (!entity) {
						return;
					}

					const currentUrl = window.location.href;

					if (currentUrl.includes('/list')) {
						let errorMessage = '';
						if (stompResponse?.type === 'PersistFrame') {
							errorMessage = $localize`:i18n=@@entity.action.persist.error.message:
																			${entity.label}:ENTITY_NAME:`;
							this.store.dispatch(
								persistEntityStopped({
									entityId: entity.id,
									jobId: stompResponse.data.job_id,
								})
							);
						}

						if (stompResponse?.type === 'UnPersistFrame') {
							errorMessage = $localize`:i18n=@@entity.action.unpersist.error.message:
																			${entity.label}:ENTITY_NAME:`;
							this.store.dispatch(
								unpersistEntityStopped({
									entityId: entity.id,
									jobId: stompResponse.data.job_id,
								})
							);
						}

						if (stompResponse?.type === 'RepersistFrame') {
							errorMessage = $localize`:i18n=@@entity.action.repersist.error.message:
																			${entity.label}:ENTITY_NAME:`;
							this.store.dispatch(
								repersistEntityStopped({
									entityId: entity.id,
									jobId: stompResponse.data.job_id,
								})
							);
						}

						this.ngCommService.notifyOnError(errorMessage);
					}
				}
			}),
			map(([stompResponse]) => stompResponse),
			filter(
				(stompResponse) =>
					stompResponse?.data?.job_status.toLocaleLowerCase() !== 'running' &&
					stompResponse?.data?.job_status.toLocaleLowerCase() !== 'waiting'
			),
			map((stompResponse) =>
				persistenceActionFinish({
					entityId: stompResponse.element_id,
					persistenceInfo: {
						isPersisted: stompResponse.data?.persisted ?? false,
						persistenceDuration:
							stompResponse.data?.persistence_execution_time ?? 0,
						persistenceSize: stompResponse.data?.persistence_size ?? 0,
						persistenceDate: stompResponse.data?.persistence_date
							? new Date(stompResponse.data.persistence_date)
							: null,
						partitionCount: stompResponse.data?.current_repartition_number ?? 0,
					},
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(refreshEntityList());
				return caught;
			})
		)
	);

	public initEntityList$ = createEffect(() =>
		this.action$.pipe(
			ofType(constructEntityListSuccess),
			switchMap(() => this.entityRequester.getEntities()),
			map((response) =>
				fetchEntityListSuccess({
					entityList: this.entityAdapter.generateEntityList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchEntityListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshEntityList$ = createEffect(() =>
		this.action$.pipe(
			ofType(refreshEntityList),
			switchMap(() => this.entityRequester.getEntities(false)),
			map((response) =>
				fetchEntityListSuccess({
					entityList: this.entityAdapter.generateEntityList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchEntityListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public persistEntities$ = createEffect(() =>
		this.action$.pipe(
			ofType(persistEntities),
			withLatestFrom(this.entitySelector.getEntityList$()),
			exhaustMap(([action, list]) =>
				from(this.resolveEntities(action.entityIds, list)).pipe(
					concatMap((entity) =>
						(entity.persistenceState.isPersisted
							? this.dataFrameRequester.repersist(entity.code)
							: this.dataFrameRequester.persist(entity.code)
						).pipe(
							map((payload) =>
								trackAction({
									entityId: entity.id,
									jobId: payload.job_id,
								})
							),
							catchError((error: HttpErrorResponse) => {
								console.error(error);
								if (window.location.href.includes('/list')) {
									this.ngCommService.notifyOnError(
										entity.persistenceState.isPersisted
											? $localize`:i18n=@@entity.action.repersist.error.message:
																			${entity.label}:ENTITY_NAME:`
											: $localize`:i18n=@@entity.action.persist.error.message:
																			${entity.label}:ENTITY_NAME:`
									);
								}
								if (entity.persistenceState.isPersisted) {
									return of(
										repersistEntityFail({
											error,
										})
									);
								}
								return of(
									persistEntityFail({
										error,
									})
								);
							})
						)
					),
					endWith(persistenceActionsAccepted())
				)
			)
		)
	);

	public repersistEntities$ = createEffect(() =>
		this.action$.pipe(
			ofType(repersistEntities),
			withLatestFrom(this.entitySelector.getEntityList$()),
			exhaustMap(([action, list]) =>
				from(this.resolveEntities(action.entityIds, list)).pipe(
					concatMap((entity) =>
						this.dataFrameRequester.repersist(entity.code).pipe(
							map((payload) =>
								trackAction({
									entityId: entity.id,
									jobId: payload.job_id,
								})
							),
							catchError((error: HttpErrorResponse) => {
								console.error(error);
								if (window.location.href.includes('/list')) {
									this.ngCommService.notifyOnError(
										$localize`:i18n=@@entity.action.repersist.error.message:
																			${entity.label}:ENTITY_NAME:`
									);
								}
								return of(
									repersistEntityFail({
										error,
									})
								);
							})
						)
					),
					endWith(persistenceActionsAccepted())
				)
			)
		)
	);

	public unpersistEntities$ = createEffect(() =>
		this.action$.pipe(
			ofType(unpersistEntities),
			withLatestFrom(this.entitySelector.getEntityList$()),
			exhaustMap(([action, list]) =>
				from(
					this.resolveEntities(action.entityIds, list).filter(
						(entity) => entity.persistenceState.isPersisted
					)
				).pipe(
					concatMap((entity) =>
						this.dataFrameRequester.unpersist(entity.code).pipe(
							map((payload) =>
								trackAction({
									entityId: entity.id,
									jobId: payload.job_id,
								})
							),
							catchError((error: HttpErrorResponse) => {
								console.error(error);
								if (window.location.href.includes('/list')) {
									this.ngCommService.notifyOnError(
										$localize`:i18n=@@entity.action.unpersist.error.message:
																			${entity.label}:ENTITY_NAME:`
									);
								}
								return of(
									unpersistEntityFail({
										error,
									})
								);
							})
						)
					),
					endWith(persistenceActionsAccepted())
				)
			)
		)
	);

	public deleteEntity$ = createEffect(() =>
		this.action$.pipe(
			ofType(deleteEntity),
			switchMap((action) => this.entityRequester.deleteEntity(action.entityId)),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@entity.delete.success:`
				);
				return deleteEntitySuccess();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@entity.delete.error:`
				);
				this.store.dispatch(deleteEntityFail());
				return caught;
			})
		)
	);

	public bulkDeleteEntities$ = createEffect(() =>
		this.action$.pipe(
			ofType(bulkDelete),
			switchMap((action) => this.entityRequester.deleteMany(action.entityId)),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@entity.delete.multiple.success:`
				);
				return bulkDeleteSuccess();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@entity.delete.multiple.error:`
				);
				this.store.dispatch(bulkDeleteFail());
				return caught;
			})
		)
	);

	public createDBsFromEntities$ = createEffect(() =>
		this.action$.pipe(
			ofType(createDatablocksFromEntities),
			switchMap((action) =>
				this.entityRequester.createDatablocksFromEntities(action.entityId)
			),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@entity.createDatablocks.success:`
				);
				return createDatablocksFromEntitiesSuccess();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(' cannot create datablocks'); // FIXME
				this.store.dispatch(createDatablocksFromEntitiesFail());
				return caught;
			})
		)
	);

	public toggleActivationStatus$ = createEffect(() =>
		this.action$.pipe(
			ofType(toggleEntityStatus),
			switchMap((action) => this.entityRequester.getEntity(action.entityId)),
			switchMap((entity) =>
				this.entityRequester.updateEntity({
					...entity,
					active: !entity.active,
				})
			),
			map(() => refreshEntityList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(refreshEntityList());
				return caught;
			})
		)
	);

	// ##########################################################
	// Confirmation popup effects
	// ##########################################################

	public stopOngoingPersistence$ = createEffect(() =>
		this.action$.pipe(
			ofType(stopPersistence),
			switchMap((action) =>
				this.entityRequester.stopPersistence(action.jobKey)
			),
			map(() => refreshEntityList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					persistEntityFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public constructor(
		private readonly action$: Actions,
		private readonly entitySelector: EntitySelector,
		private readonly appSelector: AppSelector,
		private readonly entityRequester: EntityRequester,
		private readonly dataFrameRequester: DataFrameRequester,
		private readonly entityAdapter: EntityAdapter,
		private readonly stateService: AjsStateService,
		private readonly store: Store,
		private readonly ngCommService: NgCommService,
		private readonly ajsScopeService: AjsScopeService
	) {}

	private resolveEntities(
		ids: Array<number>,
		list: EntityListEntity
	): Array<EntityItemEntity> {
		return ids
			.map((id) => list.elements.get(`${id}`))
			.filter((entity): entity is EntityItemEntity => entity !== undefined);
	}
}
